import React, { useEffect, useState } from 'react';
import {
  Grid,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TableRow,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

import { USER_WALLET_REFUNDS } from '#root/constants/navigation';
import { userService } from '#root/services';
import { useDialog, useAuth } from '#root/hooks';
import { PaymentCheckoutDialog } from '#root/components';
import { GuveryLayout, GuveryPageHeader, GuveryDialog, GuveryTabPanel } from '#root/ui-components';
import { GuveryPaper } from '#root/ui-components/cards';
import { GuveryTableCell, SkeletonRow } from '#root/ui-components/Table';
import { Button } from '#root/components/ui';
import { TrendingDown, TrendingUp } from '#root/icons';
import type { DataSourceType } from '#root/interfaces/PaymentCheckoutDialog';
import WalletBalanceCard from './WalletBalanceCard';
import WalletInfoPanel from './WalletInfoPanel';
import './userWallet.scss';

// --- Styled Tabs (pill style, custom para esta vista) ---

// @ts-ignore
const WalletTabs = withStyles({
  root: {
    height: '38px',
    minHeight: '38px',
  },
  indicator: {
    height: '38px',
    minHeight: '38px',
    backgroundColor: 'var(--color-primary)',
    borderRadius: '6px',
  },
})((props: any) => <Tabs {...props} />);

// @ts-ignore
const WalletTab = withStyles((theme: any) => ({
  root: {
    minHeight: '38px',
    minWidth: 'auto',
    textTransform: 'none' as const,
    fontWeight: 600,
    fontSize: '14px',
    marginRight: theme.spacing(1),
    transition: 'color 0.2s ease 0s',
    '&:hover': {
      opacity: 1,
    },
  },
  selected: {
    color: 'rgb(255, 255, 255)',
    zIndex: 5,
  },
}))((props: any) => <Tab disableRipple {...props} />);

// --- Types ---

interface WalletTransaction {
  feccre: string;
  con: string;
  tip: 'INGRESO' | 'EGRESO';
  mon: string;
  imp: string;
  idx: string;
  channel_with_bank: string;
}

interface WalletData {
  transactions_created: WalletTransaction[];
  transactions_invalid: WalletTransaction[];
  transactions_valid: WalletTransaction[];
}

interface PaymentCheckoutData {
  banks?: any;
  currencies?: any;
  zipago?: any;
  show_credits?: boolean;
}

// --- Helpers ---

const a11yProps = (index: number) => ({
  id: `wallet-tab-${index}`,
  'aria-controls': `wallet-tabpanel-${index}`,
});

// --- Sub-components ---

const AmountText = ({ currency, amount, balance }: { currency: string; amount: string; balance: string }) => {
  const variantCurrency: Record<string, string> = { SOLES: 'PEN', DOLARES: 'USD' };
  const variantClassName: Record<string, string> = { INGRESO: 'success', EGRESO: 'error' };
  const variantSuffix: Record<string, string> = { INGRESO: '+', EGRESO: '-' };

  return (
    <span className={`amount-text amount-text--${variantClassName[balance]}`}>
      {`${variantSuffix[balance]} ${amount} `}
      <span className="currency">{variantCurrency[currency]}</span>
    </span>
  );
};

const WalletRow = ({ row }: { row: WalletTransaction }) => {
  let idx = '#' + row.idx;
  if (row.con === 'RECARGA SALDO' || row.con === 'REEMBOLSO COMPRADOR') {
    idx = '';
  }
  return (
    <TableRow hover>
      <TableCell>
        {format(new Date(row.feccre.replace(/ /g, 'T')), "dd 'de' MMMM 'del' yyyy", {
          locale: es,
        })}
      </TableCell>
      <TableCell>
        <div className="desc-wrap">
          <div
            className={`icon ${row.tip === 'INGRESO' ? 'icon--success' : ''} ${
              row.tip === 'EGRESO' ? 'icon--error' : ''
            }`}>
            {row.tip === 'INGRESO' && <TrendingUp />}
            {row.tip === 'EGRESO' && <TrendingDown />}
          </div>
          <div className="name">
            <span>{row.con} </span>
            {idx}
          </div>
        </div>
      </TableCell>
      <TableCell>{row.channel_with_bank}</TableCell>
      <TableCell>
        <AmountText currency={row.mon} amount={row.imp} balance={row.tip} />
      </TableCell>
    </TableRow>
  );
};

interface WalletTransactionsTableProps {
  data: WalletTransaction[];
  loading: boolean;
}

const WalletTransactionsTable = ({ data, loading }: WalletTransactionsTableProps) => (
  <>
    <TableContainer>
      <Table style={{ minWidth: 650 }} aria-label="wallet table">
        <TableHead>
          <TableRow>
            <GuveryTableCell>Fecha</GuveryTableCell>
            <GuveryTableCell>Descripción</GuveryTableCell>
            <GuveryTableCell>Banco</GuveryTableCell>
            <GuveryTableCell>Importe</GuveryTableCell>
          </TableRow>
        </TableHead>
        {!loading ? (
          <TableBody>
            {data.map((row, index) => (
              <WalletRow row={row} key={index} />
            ))}
          </TableBody>
        ) : (
          <TableBody>
            {Array.from(new Array(10)).map((_data, index) => (
              <SkeletonRow columnsCount={4} key={index} />
            ))}
          </TableBody>
        )}
      </Table>
    </TableContainer>
    {data.length <= 0 && !loading && (
      <div className="no-content">
        <p>No hay transacciones</p>
      </div>
    )}
  </>
);

// --- Dialogs ---

const RechargeDialog = ({
  open = false,
  onClose,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) => (
  <GuveryDialog open={open} onClose={onClose} title="Recargar mi saldo" closeText="Cerrar">
    <div className="dialog-content-recharge">
      <p className="guv-font-size-5 guv-text-darker guv-regular">
        Recarga tu saldo para que puedas pagar tus acuerdos más rápido.
      </p>
      <div className="actions">
        <p className="guv-font-size-5">
          Puedes solicitar el retiro de tu saldo a favor cuando lo desees.
        </p>
        <Button fullWidth variant="primary" onClick={onConfirm}>
          Hacer una recarga
        </Button>
      </div>
    </div>
  </GuveryDialog>
);

const RetireDialog = ({ open = false, onClose }: { open: boolean; onClose: () => void }) => (
  <GuveryDialog open={open} onClose={onClose} title="Retirar mi saldo" closeText="Cerrar">
    <div className="dialog-content-retire">
      <p className="guv-font-size-5 guv-regular">
        Antes de solicitar el retiro de tu saldo, recuerda que mantener un saldo a favor en Guvery
        te ayudará a pagar más rapido tus acuerdos.
      </p>
      <div className="actions">
        <p className="guv-font-size-5">
          Puedes solicitar el retiro de tu saldo cuando lo desees
        </p>
        <p className="guv-font-size-5">Escríbenos a nuestro Whatsapp:</p>
        <a
          className="guv-font-size-5 guv-no-decoration"
          href="https://api.whatsapp.com/send?phone=51960684330&text=Hola!"
          target="_blank"
          rel="noopener noreferrer">
          +51 960684330
        </a>
        <p className="guv-font-size-5" style={{ marginTop: 8 }}>
          Lunes a Viernes de 9am a 6pm.
        </p>
        <Button fullWidth variant="primary" onClick={onClose}>
          Entendido
        </Button>
      </div>
    </div>
  </GuveryDialog>
);

// --- Main Component ---

function UserWallet() {
  const retireDialog = useDialog();
  const rechargeDialog = useDialog();
  const { usuario } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(1);
  const [wallet, setWallet] = useState<WalletData>({
    transactions_created: [],
    transactions_invalid: [],
    transactions_valid: [],
  });

  // Payment vars
  const paymentDialog = useDialog();
  const [paymentData, setPaymentData] = useState<PaymentCheckoutData | null>(null);
  const [paymentDialogSource, setPaymentDialogSource] = useState<DataSourceType>({
    title: 'Recargar Saldo',
    id: 0,
    amount: 0,
    amountHelper: 'Importe de la recarga:',
    currency: 'DOLARES',
    tca: 0,
    disabledCurrencies: false,
    disabledPrice: false,
    allowConvert: false,
    allowCredits: false,
    allowCoupon: false,
    allowZipago: true,
    showCredits: true,
    stepsArray: [
      'Selecciona el método y el banco.',
      'Escoge la moneda y el monto.',
      'Realiza el pago y confírmalo.',
    ],
    methodsHelper: '',
    paymentType: 'WALLET',
  });

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
  };

  const openPaymentDialog = () => {
    paymentDialog.openDialog();
  };

  const closePaymentDialog = () => {
    fetchData();
    paymentDialog.closeDialog();
  };

  const fetchData = async () => {
    setLoading(true);
    const res = await userService.wallet();
    if (!res.error) {
      const data = res.data;
      if (data?.bank_transactions?.transactions_created.length > 0) {
        setTabValue(0);
      }
      setWallet(data.bank_transactions);
      setPaymentData(data.payment_checkout_dialog);
      setPaymentDialogSource((prev) => ({
        ...prev,
        allowZipago: data?.payment_checkout_dialog?.zipago ? true : false,
        showCredits: data?.payment_checkout_dialog?.show_credits,
      }));
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabData = [
    { label: 'En Proceso', data: wallet.transactions_created },
    { label: 'Válidos', data: wallet.transactions_valid },
    { label: 'No Válidos', data: wallet.transactions_invalid },
  ];

  return (
    <GuveryLayout className="user-wallet">
      <GuveryPageHeader
        title="Mi Billetera"
        extra={[
          <Button key="1" variant="outline" size="sm" to={USER_WALLET_REFUNDS}>
            Reembolsos
          </Button>,
          <Button key="2" variant="primary" onClick={openPaymentDialog} size="sm">
            Recargar
          </Button>,
        ]}
      />
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <WalletBalanceCard
            saldo={usuario.saldo}
            saldoEstado={usuario.saldo_est}
            saldoEvento={usuario.saldo_evento}
          />
        </Grid>

        <Grid item xs={12}><WalletInfoPanel /></Grid>

        <Grid item xs={12}>
          <GuveryPaper disableHover disableElevation className="card-movements">
            <div className="header">
              <h4>Movimientos</h4>
              <WalletTabs value={tabValue} onChange={handleTabChange} aria-label="tabs wallet types">
                {tabData.map((tab, idx) => (
                  <WalletTab key={idx} label={tab.label} {...a11yProps(idx)} />
                ))}
              </WalletTabs>
            </div>
            {tabData.map((tab, idx) => (
              <GuveryTabPanel key={idx} value={tabValue} index={idx}>
                <WalletTransactionsTable data={tab.data} loading={loading} />
              </GuveryTabPanel>
            ))}
          </GuveryPaper>
        </Grid>
      </Grid>
      <RechargeDialog
        open={rechargeDialog.open}
        onClose={rechargeDialog.closeDialog}
        onConfirm={openPaymentDialog}
      />
      <RetireDialog open={retireDialog.open} onClose={retireDialog.closeDialog} />
      <PaymentCheckoutDialog
        open={paymentDialog.open}
        onClose={closePaymentDialog}
        dataSource={paymentDialogSource}
        banks={paymentData?.banks}
        currencies={paymentData?.currencies}
        zipago={paymentData?.zipago}
      />
    </GuveryLayout>
  );
}

export default UserWallet;
